import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Users, 
  UserCheck, 
  Building, 
  Mail, 
  Phone,
  Save,
  X,
  Filter,
  Download
} from 'lucide-react';
import { useUsers } from '../contexts/UserContext';

const UserManagement = ({ onBack }) => {
  const { users, loading, createUser, updateUser, deleteUser, getUserStats, getAllDesignations, getAllSpecializations, getAllRolesForUserManagement } = useUsers();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterDesignation, setFilterDesignation] = useState('all');
  const [filterSpecialization, setFilterSpecialization] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'employee',
    department: '',
    designation: '',
    specialization: '',
    isActive: true
  });
  const [errors, setErrors] = useState({});
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    usersByDepartment: {},
    usersByRole: {}
  });

  useEffect(() => {
    const loadStats = async () => {
      try {
        const userStats = await getUserStats();
        if (userStats) {
          setStats({
            totalUsers: userStats.totalUsers || 0,
            activeUsers: userStats.activeUsers || 0,
            usersByDepartment: userStats.usersByDepartment || {},
            usersByRole: userStats.usersByRole || {}
          });
        }
      } catch (error) {
        console.error('Error loading user stats:', error);
        // Keep default stats
      }
    };

    loadStats();
  }, [getUserStats, users]);

  useEffect(() => {
    if (editingUser) {
      // Validate role - only allow valid enum values
      const validRoles = ['admin', 'user', 'employee', 'manager', 'hr'];
      const userRole = editingUser.role;
      const safeRole = validRoles.includes(userRole) ? userRole : 'employee';
      
      setFormData({
        name: editingUser.name,
        email: editingUser.email,
        role: safeRole,
        department: editingUser.department,
        designation: editingUser.designation || '',
        specialization: editingUser.specialization || '',
        isActive: editingUser.isActive
      });
    }
  }, [editingUser]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.department.trim()) newErrors.department = 'Department is required';
    
    // Check for duplicate email
    const safeUsers = Array.isArray(users) ? users : [];
    const existingUser = safeUsers.find(user => 
      user.email === formData.email && (user._id || user.id) !== (editingUser?._id || editingUser?.id)
    );
    if (existingUser) newErrors.email = 'Email already exists';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreate = () => {
    setFormData({
      name: '',
      email: '',
      role: 'employee',
      department: '',
      designation: '',
      specialization: '',
      isActive: true
    });
    setErrors({});
    setEditingUser(null);
    setShowCreateForm(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setShowCreateForm(true);
  };

  const handleSave = () => {
    if (!validateForm()) return;

    // Ensure role is a valid enum value
    const validRoles = ['admin', 'user', 'employee', 'manager', 'hr'];
    const safeFormData = {
      ...formData,
      role: validRoles.includes(formData.role) ? formData.role : 'employee'
    };

    if (editingUser) {
      updateUser(editingUser._id || editingUser.id, safeFormData);
    } else {
      createUser(safeFormData);
    }

    setShowCreateForm(false);
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      role: 'employee',
      department: '',
      isActive: true
    });
    setErrors({});
  };

  const handleDelete = (userId) => {
    deleteUser(userId);
    setShowDeleteConfirm(null);
  };

  const handleCancel = () => {
    setShowCreateForm(false);
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      role: 'employee',
      department: '',
      designation: '',
      specialization: '',
      isActive: true
    });
    setErrors({});
  };

  // Ensure users is always an array
  const safeUsers = Array.isArray(users) ? users : [];
  
  const filteredUsers = safeUsers
    .filter(user => {
      const matchesSearch = (user.name && user.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
                           (user.email && user.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
                           (user.department && user.department.toLowerCase().includes(searchQuery.toLowerCase())) ||
                           (user.designation && user.designation.toLowerCase().includes(searchQuery.toLowerCase())) ||
                           (user.specialization && user.specialization.toLowerCase().includes(searchQuery.toLowerCase()));
      
      const matchesRole = filterRole === 'all' || user.role === filterRole;
      const matchesDepartment = filterDepartment === 'all' || user.department === filterDepartment;
      const matchesDesignation = filterDesignation === 'all' || user.designation === filterDesignation;
      const matchesSpecialization = filterSpecialization === 'all' || user.specialization === filterSpecialization;
      
      return matchesSearch && matchesRole && matchesDepartment && matchesDesignation && matchesSpecialization;
    })
    .sort((a, b) => {
      let aValue = a[sortBy] || '';
      let bValue = b[sortBy] || '';
      
      if (sortBy === 'name') {
        aValue = (a.name || a.email || '').toLowerCase();
        bValue = (b.name || b.email || '').toLowerCase();
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return '#dc2626';
      case 'employee': return '#059669';
      case 'manager': return '#2563eb';
      case 'hr': return '#7c3aed';
      default: return '#6b7280';
    }
  };

  const getDepartmentColor = (department) => {
    const colors = ['#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#10b981', '#f97316'];
    const hash = department.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    return colors[Math.abs(hash) % colors.length];
  };

  const formatDate = (dateValue) => {
    if (!dateValue) {
      return 'N/A';
    }
    
    try {
      let date;
      
      if (dateValue instanceof Date) {
        date = dateValue;
      } else if (typeof dateValue === 'string') {
        // Check if it's a numeric string (timestamp)
        const numericValue = Number(dateValue);
        if (!isNaN(numericValue) && isFinite(numericValue)) {
          // It's a numeric string representing a timestamp
          date = new Date(numericValue);
        } else {
          // Try parsing as ISO string
          const cleanDate = dateValue.trim();
          // Handle both +00:00 and Z timezone formats
          const normalizedDate = cleanDate.replace(/\+00:00$/, 'Z');
          date = new Date(normalizedDate);
        }
      } else if (typeof dateValue === 'number') {
        // Handle numeric timestamp
        date = new Date(dateValue);
      } else {
        return 'N/A';
      }
      
      if (isNaN(date.getTime())) {
        return 'N/A';
      }
      
      // Format as MM/DD/YYYY
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    } catch (e) {
      console.error('Error formatting date:', e, 'Value:', dateValue);
      return 'N/A';
    }
  };

  const getAllDepartments = () => {
    return [...new Set(users.map(user => user.department))];
  };

  const exportUsers = () => {
    const csvContent = [
      ['Name', 'Email', 'Role', 'Department', 'Designation', 'Specialization', 'Status', 'Created At'],
      ...filteredUsers.map(user => [
        user.name,
        user.email,
        user.role,
        user.department,
        user.designation || 'N/A',
        user.specialization || 'N/A',
        user.isActive ? 'Active' : 'Inactive',
        formatDate(user.createdAt)
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'users-export.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (showCreateForm) {
    return (
      <div className="container">
        <div className="user-form-header">
          <button onClick={handleCancel} className="btn btn-secondary">
            <ArrowLeft size={16} style={{ marginRight: '8px' }} />
            Back to Users
          </button>
          <h1 className="text-2xl font-bold">
            {editingUser ? 'Edit User' : 'Create New User'}
          </h1>
        </div>

        <div className="user-form">
          <div className="form-section">
            <h2 className="text-xl font-bold mb-4">User Information</h2>
            <div className="form-grid">
              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={errors.name ? 'error' : ''}
                  placeholder="Enter full name"
                />
                {errors.name && <span className="error-text">{errors.name}</span>}
              </div>

              <div className="form-group">
                <label>Email Address *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={errors.email ? 'error' : ''}
                  placeholder="Enter email address"
                />
                {errors.email && <span className="error-text">{errors.email}</span>}
              </div>

              <div className="form-group">
                <label>Role *</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                >
                  <option value="">Select a role</option>
                  {getAllRolesForUserManagement().map(role => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Department *</label>
                <input
                  type="text"
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  className={errors.department ? 'error' : ''}
                  placeholder="Enter department"
                />
                {errors.department && <span className="error-text">{errors.department}</span>}
              </div>

              <div className="form-group">
                <label>Designation</label>
                <input
                  type="text"
                  value={formData.designation}
                  onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                  placeholder="Enter designation (e.g., Software Developer, IT Operations Manager, Financial Analyst, Administrative Officer)"
                />
              </div>

              <div className="form-group">
                <label>Specialization</label>
                <input
                  type="text"
                  value={formData.specialization}
                  onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
                  placeholder="Enter specialization (e.g., Full Stack Development, Infrastructure Management, Financial Reporting, Administrative Management)"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
                <span className="ml-2">Active User</span>
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button onClick={handleCancel} className="btn btn-secondary">
              Cancel
            </button>
            <button onClick={handleSave} className="btn btn-primary">
              <Save size={16} style={{ marginRight: '8px' }} />
              {editingUser ? 'Update User' : 'Create User'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="user-management-header">
        <button onClick={onBack} className="btn btn-secondary back-to-dashboard">
          <ArrowLeft size={16} style={{ marginRight: '8px' }} />
          Back to Dashboard
        </button>
        <div>
          <h1 className="text-3xl font-bold">User Management</h1>
          <p className="text-lg text-gray-600">Manage staff and their training progress</p>
        </div>
        <button onClick={handleCreate} className="btn btn-primary">
          <Plus size={16} style={{ marginRight: '8px' }} />
          Add New User
        </button>
      </div>

      <div className="user-stats">
        <div className="grid grid-4">
          <div className="card stat-card">
            <div className="stat-icon">
              <Users size={24} color="#3b82f6" />
            </div>
            <div className="stat-content">
              <h3 className="text-xl font-bold">{stats.totalUsers}</h3>
              <p className="text-gray-600">Total Users</p>
            </div>
          </div>
          <div className="card stat-card">
            <div className="stat-icon">
              <UserCheck size={24} color="#059669" />
            </div>
            <div className="stat-content">
              <h3 className="text-xl font-bold">{stats.activeUsers}</h3>
              <p className="text-gray-600">Active Users</p>
            </div>
          </div>
          <div className="card stat-card">
            <div className="stat-icon">
              <Building size={24} color="#f59e0b" />
            </div>
            <div className="stat-content">
              <h3 className="text-xl font-bold">{Object.keys(stats.usersByDepartment || {}).length}</h3>
              <p className="text-gray-600">Departments</p>
            </div>
          </div>
          <div className="card stat-card">
            <div className="stat-icon">
              <Users size={24} color="#8b5cf6" />
            </div>
            <div className="stat-content">
              <h3 className="text-xl font-bold">{Object.keys(stats.usersByRole || {}).length}</h3>
              <p className="text-gray-600">Roles</p>
            </div>
          </div>
        </div>
      </div>

      <div className="user-controls">
        <div className="search-filters">
          <div className="search-container">
            <Search size={16} className="search-icon" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="filters">
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Roles</option>
              <option value="admin">Admin</option>
              <option value="employee">Employee</option>
              <option value="manager">Manager</option>
              <option value="hr">HR</option>
            </select>
            
            <select
              value={filterDepartment}
              onChange={(e) => setFilterDepartment(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Departments</option>
              {getAllDepartments().map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
            
            <select
              value={filterDesignation}
              onChange={(e) => setFilterDesignation(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Designations</option>
              {getAllDesignations().map(designation => (
                <option key={designation} value={designation}>{designation}</option>
              ))}
            </select>
            
            <select
              value={filterSpecialization}
              onChange={(e) => setFilterSpecialization(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Specializations</option>
              {getAllSpecializations().map(specialization => (
                <option key={specialization} value={specialization}>{specialization}</option>
              ))}
            </select>
            
            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [field, order] = e.target.value.split('-');
                setSortBy(field);
                setSortOrder(order);
              }}
              className="filter-select"
            >
              <option value="name-asc">Name (A-Z)</option>
              <option value="name-desc">Name (Z-A)</option>
              <option value="email-asc">Email (A-Z)</option>
              <option value="email-desc">Email (Z-A)</option>
              <option value="department-asc">Department (A-Z)</option>
              <option value="department-desc">Department (Z-A)</option>
            </select>
          </div>
        </div>
        
        <button onClick={exportUsers} className="btn btn-secondary">
          <Download size={16} style={{ marginRight: '8px' }} />
          Export CSV
        </button>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Department</th>
              <th>Designation</th>
              <th>Specialization</th>
              <th>Status</th>
              {/* <th>Created</th> */}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => {
              // Debug: Log user data to see what we're receiving
              if (filteredUsers.indexOf(user) === 0) {
                console.log('Sample user data:', user);
                console.log('user.name:', user.name, 'Type:', typeof user.name);
                console.log('user.email:', user.email);
                console.log('user.createdAt:', user.createdAt, 'Type:', typeof user.createdAt);
              }
              return (
              <tr key={user._id || user.id}>
                <td>
                  <div className="user-info">
                    <div className="user-avatar">
                      <UserCheck size={16} color={getRoleColor(user.role)} />
                    </div>
                    <div className="user-name">{user.name || user.email || 'N/A'}</div>
                  </div>
                </td>
                <td>
                  <div className="user-email">
                    <Mail size={12} />
                    {user.email}
                  </div>
                </td>
                <td>
                  <span 
                    className="role-badge"
                    style={{ backgroundColor: getRoleColor(user.role) + '20', color: getRoleColor(user.role) }}
                  >
                    {user.role}
                  </span>
                </td>
                <td>
                  <span 
                    className="department-badge"
                    style={{ backgroundColor: getDepartmentColor(user.department) + '20', color: getDepartmentColor(user.department) }}
                  >
                    <Building size={12} />
                    {user.department}
                  </span>
                </td>
                <td>
                  <span className="designation-badge">
                    {user.designation || 'N/A'}
                  </span>
                </td>
                <td>
                  <span className="specialization-badge">
                    {user.specialization || 'N/A'}
                  </span>
                </td>
                <td>
                  <span className={`status-badge ${user.isActive ? 'active' : 'inactive'}`}>
                    {user.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                {/* <td>
                  {formatDate(user.createdAt)}
                </td> */}
                <td>
                  <div className="action-buttons">
                    <button
                      onClick={() => handleEdit(user)}
                      className="btn-icon"
                      title="Edit User"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(user._id || user.id)}
                      className="btn-icon btn-danger"
                      title="Delete User"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            );
            })}
          </tbody>
        </table>
        
        {filteredUsers.length === 0 && !loading && (
          <div className="no-users">
            <Users size={48} className="text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-600 mb-2">No Users Found</h3>
            <p className="text-gray-500">
              {searchQuery || filterRole !== 'all' || filterDepartment !== 'all' 
                ? 'No users match your search criteria.' 
                : 'No users have been added yet.'}
            </p>
          </div>
        )}
      </div>

      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Confirm Delete</h3>
              <button onClick={() => setShowDeleteConfirm(null)} className="btn-icon">
                <X size={16} />
              </button>
            </div>
            <div className="modal-content">
              <p>Are you sure you want to delete this user? This action cannot be undone.</p>
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowDeleteConfirm(null)} className="btn btn-secondary">
                Cancel
              </button>
              <button onClick={() => handleDelete(showDeleteConfirm)} className="btn btn-danger">
                <Trash2 size={16} style={{ marginRight: '8px' }} />
                Delete User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
